// src/pages/ResetDataPage.tsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useData } from "../DataContext";
import { Card } from "../components/ui/Card";
import { apiRequest } from "../api/http";

type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: string };

function hardReloadHome() {
  window.location.href = "/";
}

export default function ResetDataPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    authSession,
    tenantId,
    activeTenant,
    resetAllData,
    // (optional: can be used after wipe to refetch)
    // note: DataContext reloads on tenant changes already
  } = useData() as any;

  const isAdmin = authSession?.role === "admin";

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>("");

  const adminKey = useMemo(() => {
    try {
      return window.localStorage.getItem("njdc_admin_key") || "";
    } catch {
      return "";
    }
  }, []);

  const activeTenantId = activeTenant?.tenantId || tenantId || "demo";

  const sqlWipeSnippet = useMemo(() => {
    // Handy fallback if you prefer wrangler d1 execute
    const t = activeTenantId;
    return `-- Run this in Wrangler / D1 console to wipe one tenant
DELETE FROM projects              WHERE tenant_id = '${t}';
DELETE FROM expenses              WHERE tenant_id = '${t}';
DELETE FROM payments_received     WHERE tenant_id = '${t}';
DELETE FROM scope                 WHERE tenant_id = '${t}';
DELETE FROM resources             WHERE tenant_id = '${t}';
DELETE FROM wages                 WHERE tenant_id = '${t}';
DELETE FROM partners              WHERE tenant_id = '${t}';
DELETE FROM partner_disbursements WHERE tenant_id = '${t}';`;
  }, [activeTenantId]);

  const onResetLocal = () => {
    const ok = window.confirm(t("resetData.confirmResetLocal", { tenant: activeTenantId }));
    if (!ok) return;

    resetAllData();
    setMsg(t("resetData.localCleared"));
    setTimeout(() => hardReloadHome(), 250);
  };

  const onWipeTenantDb = async () => {
    if (!isAdmin) {
      alert(t("resetData.adminOnly"));
      return;
    }
    if (!adminKey) {
      alert(t("resetData.missingAdminKey"));
      return;
    }

    const ok = window.confirm(t("resetData.confirmWipeTenant", { tenant: activeTenantId }));
    if (!ok) return;

    setBusy(true);
    setMsg("");

    try {
      const res = await apiRequest<ApiOk<any> | ApiErr>(
        "POST",
        "/api/admin/wipe_tenant",
        {
          tenantId: activeTenantId,
          headers: { "x-admin-key": adminKey },
          body: {},
        }
      );

      if ((res as any)?.ok) {
        setMsg(`DB wiped for tenant "${activeTenantId}".`);
        // Also clear local fallback to avoid mixing old UI data
        resetAllData();
        setTimeout(() => hardReloadHome(), 350);
      } else {
        setMsg((res as any)?.error || "DB wipe failed.");
      }
    } catch (e: any) {
      setMsg(e?.message || "DB wipe failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container">
      <div className="pageHeader">
        <h2 className="pageTitle">{t("resetData.title")}</h2>
        <div className="pageSub">{t("resetData.subtitle")}</div>
      </div>

      <Card
        title={t("resetData.currentScopeTitle")}
        subtitle={t("resetData.currentScopeSub")}
      >
        <div className="ovMiniRow" style={{ marginTop: 8 }}>
          <div className="ovMiniLabel">{t("resetData.activeTenantLabel")}</div>
          <div className="ovMiniValue">
            {activeTenant?.tenantName || activeTenantId}{" "}
            <span className="ovMiniOutOf">
              (<code>{activeTenantId}</code>)
            </span>
          </div>
        </div>

        <div className="pageNote" style={{ marginTop: 12 }}>
          <b>Important:</b> {t("resetData.importantNote")}
        </div>
      </Card>

      <div style={{ display: "grid", gap: 14, marginTop: 14 }}>
        <Card
          title={t("resetData.resetLocalTitle")}
          subtitle={t("resetData.resetLocalSub")}
        >
          <div className="pageNote">
            {t("resetData.resetLocalNote")}
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
            <button className="btn btnDanger" onClick={onResetLocal} type="button">
              {t("resetData.resetLocalBtn")}
            </button>
            <button className="btn btnSecondary" onClick={() => navigate("/")} type="button">
              {t("resetData.cancelBtn")}
            </button>
          </div>
        </Card>

        <Card
          title={t("resetData.wipeTenantTitle")}
          subtitle={t("resetData.wipeTenantSub")}
        >
          {!isAdmin ? (
            <div className="noteOnCard">
              {t("resetData.adminOnlyNote")}
            </div>
          ) : null}

          <div className="pageNote" style={{ marginTop: 10 }}>
            This calls <code>/api/admin/wipe_tenant</code> and requires:
            <ul style={{ marginTop: 8 }}>
              <li>
                Cloudflare env var: <code>ADMIN_KEY</code>
              </li>
              <li>
                Browser stored key: <code>njdc_admin_key</code> (set in Admin - Tenants)
              </li>
            </ul>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
            <button
              className="btn btnDanger"
              onClick={onWipeTenantDb}
              type="button"
              disabled={!isAdmin || busy}
            >
              {busy ? t("resetData.wiping") : t("resetData.wipeTenantBtn")}
            </button>
          </div>

          {msg ? <div className="pageNote" style={{ marginTop: 10 }}>{msg}</div> : null}

          <hr className="hr" />

          <div className="pageNote">
            {t("resetData.manualSqlNote")}
            <pre style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>{sqlWipeSnippet}</pre>
          </div>
        </Card>
      </div>
    </div>
  );
}
